<script setup lang="ts">
import type { News } from '~/types/api'

type NewsCardProps = {
  news: News
  variant?: string
  showPerex?: boolean
}

const localePath = useLocalePath()
const { getResourceUrl } = useResources()

const props = withDefaults(defineProps<NewsCardProps>(), {
  variant: 'dark',
  showPerex: true,
})

const releaseDate = new Intl.DateTimeFormat('cs-CZ', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
}).format(new Date(props.news?.releaseDate))
</script>

<template>
  <article class="flex min-h-full flex-col">
    <figure
      class="relative m-0 block aspect-[3/2] w-full overflow-hidden p-0 before:block before:pt-[100%] before:content-['']"
    >
      <NuxtImg
        :src="getResourceUrl(news?.thumbnailImage?.url)"
        sizes="300px sm:600px xl:500px"
        class="absolute bottom-0 left-0 top-0 h-full w-full border-0 object-cover"
        alt=""
        title=""
        loading="lazy"
      />
      <NuxtImg
        v-if="news?.backgroundImage?.url != null"
        class="absolute bottom-0 left-0 top-0 h-full w-full border-0 object-cover"
        alt=""
        title=""
        loading="lazy"
        :src="getResourceUrl(news?.backgroundImage?.url)"
        sizes="300px lg:400px xl:500px"
        data-ll-status="loaded"
      />
    </figure>
    <div
      class="flex grow flex-col justify-between"
      :class="
        variant === 'dark'
          ? ['bg-black', 'text-white', 'p-10']
          : ['bg-white', 'text-black', 'py-10']
      "
    >
      <div class="">
        <time class="text-sm" :datetime="releaseDate">
          {{ releaseDate }}
        </time>
        <h3
          class="font-pragmatica-ext text-xl font-bold"
          :title="news.title ?? ''"
        >
          <NuxtLink
            :to="
              localePath({
                name: 'news-slug',
                params: { slug: news.urlSlug },
              })
            "
            class="text-light no-underline hover:underline"
          >
            {{ news.title }}
          </NuxtLink>
        </h3>
        <p v-if="showPerex" class="text-sm">
          {{ truncate(stripHtml(news.content ?? ''), 250) }}
        </p>
      </div>
      <div>
        <NuxtLink
          :to="
            localePath({
              name: 'news-slug',
              params: { slug: news.urlSlug },
            })
          "
          class="border-b text-sm no-underline"
        >
          {{ $t('base.news.buttons.readMore') }}
        </NuxtLink>
      </div>
    </div>
  </article>
</template>

<style lang="css"></style>
